import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  EyeOff,
  Gauge,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  TrendingUp,
  Users
} from "lucide-react";

export default function DashboardTutorialPage() {
  const { user } = useAuth();

  const modes = [
    {
      title: "Inteligente",
      description:
        "Organiza automaticamente as áreas de gestão e inteligência conforme os indicadores que merecem mais atenção."
    },
    {
      title: "Operacional",
      description:
        "Prioriza agenda, confirmações, lembretes, estoque e atividades que precisam ser resolvidas no dia."
    },
    {
      title: "Financeiro",
      description:
        "Dá maior destaque aos valores faturados, recebidos, vencidos, despesas, margem e projeções."
    },
    {
      title: "Crescimento",
      description:
        "Prioriza oportunidades, pacientes sem retorno, tratamentos parados, tendências e previsões."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">
            Ajuda
          </Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">
            Guias
          </Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Dashboard Executivo</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Dashboard Executivo
        </h1>

        <p className="text-xl text-gray-600 font-semibold mb-6">
          Uma visão inteligente da operação, do financeiro e das oportunidades da clínica
        </p>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-10">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>12 minutos de leitura</span>
          </div>

          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão e Agenda
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            O <strong>Dashboard Executivo</strong> reúne as informações mais importantes
            da clínica e transforma dados da agenda, dos pacientes, dos tratamentos,
            do estoque e do financeiro em prioridades de gestão.
          </p>

          <p>
            A proposta não é exibir todos os dados do sistema ao mesmo tempo. O painel
            destaca o que merece atenção, permite abrir diretamente a área relacionada
            e pode ser personalizado por cada usuário.
          </p>

          <div className="bg-indigo-50 border border-indigo-200 rounded-3xl p-6 my-8">
            <h3 className="text-lg font-bold text-indigo-900 mt-0 mb-2 flex items-center gap-2">
              <Sparkles size={22} />
              Comece pelo que exige ação
            </h3>
            <p className="text-indigo-800 mb-0">
              Ao entrar no sistema, consulte primeiro a <strong>Operação do dia</strong>,
              as <strong>Metas do dia</strong> e o <strong>Assistente Executivo</strong>.
              Depois, use as áreas de gestão e inteligência para análises mais amplas.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Passo a Passo em Texto
          </h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                Visão inicial e indicadores principais
              </h3>
              <p>
                O primeiro bloco apresenta uma leitura resumida da clínica. Dependendo
                dos dados disponíveis, você poderá ver indicadores como agenda do dia,
                pacientes ativos, faturamento, recebimentos, valores vencidos e score
                geral da clínica.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Gauge size={20} className="text-indigo-600" />
                    Score geral
                  </h4>
                  <p className="text-sm mb-0">
                    Resume o desempenho de agenda, financeiro, pacientes e operação.
                    Clique em <strong>Entender score</strong> para visualizar a composição
                    e os pontos que podem estar reduzindo a nota.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <EyeOff size={20} className="text-indigo-600" />
                    Privacidade financeira
                  </h4>
                  <p className="text-sm mb-0">
                    Use o ícone de olho para ocultar ou mostrar valores financeiros.
                    A escolha fica salva separadamente para cada usuário.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                Operação do dia
              </h3>
              <p>
                Esta área reúne o que precisa ser acompanhado durante a rotina diária.
                Ela permanece logo abaixo do cabeçalho, independentemente do modo
                escolhido para o Dashboard.
              </p>
              <ul>
                <li>consultas e horários do dia;</li>
                <li>confirmações pendentes;</li>
                <li>riscos de falta;</li>
                <li>lembretes e aniversários;</li>
                <li>alertas de estoque;</li>
                <li>atalhos para as áreas que precisam de intervenção.</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-5">
                <p className="text-amber-900 mb-0">
                  <strong>Importante:</strong> ao clicar em um indicador ou ação, o
                  OdontoAssist AI abre a página correspondente com o filtro aplicável,
                  como cobranças vencidas, pacientes sem retorno ou tratamentos parados.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                Metas do dia
              </h3>
              <p>
                As metas são criadas conforme a situação atual da clínica. Elas podem
                envolver confirmações, cobranças vencidas, pacientes sem retorno,
                tratamentos parados, lembretes ou estoque baixo.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                  <h4 className="font-bold text-emerald-900 flex items-center gap-2 mb-2">
                    <ListChecks size={20} />
                    Marcar como concluída
                  </h4>
                  <p className="text-sm text-emerald-800 mb-0">
                    Clique no círculo da meta para concluir. O percentual e a barra
                    de progresso são atualizados imediatamente.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Calendar size={20} className="text-indigo-600" />
                    Reinício diário
                  </h4>
                  <p className="text-sm mb-0">
                    O progresso é salvo por usuário e por data. No dia seguinte, a
                    clínica recebe uma nova lista de prioridades.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                Assistente Executivo
              </h3>
              <p>
                O Assistente Executivo interpreta os indicadores atuais e produz uma
                leitura direta da situação da clínica. Ele aponta alertas e recomenda
                ações sem depender de uma pergunta do usuário.
              </p>
              <p>Entre os assuntos analisados estão:</p>
              <ul>
                <li>cobranças vencidas;</li>
                <li>consultas aguardando confirmação;</li>
                <li>risco de faltas;</li>
                <li>pacientes sem retorno;</li>
                <li>tratamentos sem continuidade;</li>
                <li>agenda sem movimentação.</li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 mt-5">
                <p className="text-indigo-900 mb-0">
                  O Assistente Executivo trabalha com o estado atual. Para análises
                  históricas e comparações por período, utilize o Consultor de Gestão.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">5</span>
                Consultor de Gestão
              </h3>
              <p>
                O Consultor de Gestão compara indicadores financeiros e operacionais
                em períodos móveis de <strong>30, 90 ou 180 dias</strong>.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <TrendingUp size={20} className="text-indigo-600" />
                    Diagnósticos
                  </h4>
                  <p className="text-sm mb-0">
                    Faturamento, recebimentos, inadimplência, margem, resultado,
                    movimento da agenda, faltas e dias com menor ocupação.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Target size={20} className="text-indigo-600" />
                    Prioridade e impacto
                  </h4>
                  <p className="text-sm mb-0">
                    Cada diagnóstico é classificado como crítico, atenção,
                    oportunidade, positivo ou neutro e apresenta uma consequência
                    provável para a clínica.
                  </p>
                </div>
              </div>
              <p className="mt-5">
                Quando possível, o período selecionado é comparado com o intervalo
                anterior de mesma duração. Por exemplo, os últimos 30 dias podem ser
                comparados com os 30 dias imediatamente anteriores.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">6</span>
                Gestão do mês
              </h3>
              <p>
                Esta seção concentra os indicadores financeiros e gerenciais do
                período atual. Os valores dependem dos registros realizados no módulo
                Financeiro.
              </p>
              <ul>
                <li>faturado e recebido;</li>
                <li>valores pendentes e vencidos;</li>
                <li>despesas e resultado operacional;</li>
                <li>margem;</li>
                <li>projeção e evolução financeira;</li>
                <li>comparação com períodos anteriores, quando houver base.</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mt-5">
                <p className="text-blue-900 mb-0">
                  Para que os indicadores sejam confiáveis, mantenha faturas,
                  recebimentos e despesas atualizados no Financeiro.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">7</span>
                Inteligência e previsão
              </h3>
              <p>
                A área de inteligência destaca sinais que podem representar perda de
                produção ou oportunidade de crescimento.
              </p>
              <ul>
                <li>pacientes sem retorno;</li>
                <li>tratamentos parados;</li>
                <li>risco de faltas;</li>
                <li>distribuição e ocupação da agenda;</li>
                <li>tendências e oportunidades de acompanhamento.</li>
              </ul>
              <p>
                Os cards funcionam também como atalhos. Ao clicar, você será levado
                para a lista correspondente no prontuário ou na agenda.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">8</span>
                Modos do Dashboard
              </h3>
              <p>
                Os modos alteram a prioridade das áreas, sem eliminar os recursos.
                A Operação do dia continua em posição de destaque.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                {modes.map((mode) => (
                  <div
                    key={mode.title}
                    className="bg-slate-50 p-4 rounded-xl border border-slate-200"
                  >
                    <h4 className="font-bold text-indigo-700 mb-2">
                      {mode.title}
                    </h4>
                    <p className="text-sm mb-0">{mode.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">9</span>
                Personalização
              </h3>
              <p>
                Clique em <strong>Personalizar</strong> para escolher as seções e
                blocos que deseja visualizar.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <SlidersHorizontal size={20} className="text-indigo-600" />
                    Exibir ou ocultar
                  </h4>
                  <p className="text-sm mb-0">
                    Desative blocos que não fazem parte da rotina daquele usuário,
                    reduzindo o volume de informações exibidas.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Settings size={20} className="text-indigo-600" />
                    Restaurar padrão
                  </h4>
                  <p className="text-sm mb-0">
                    Use a opção de restauração para retornar à configuração inicial
                    recomendada pelo OdontoAssist AI.
                  </p>
                </div>
              </div>
              <p className="mt-5 mb-0">
                As preferências são armazenadas por usuário. Pessoas diferentes da
                mesma clínica podem utilizar organizações distintas.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">10</span>
                Rotina recomendada
              </h3>
              <div className="space-y-3 mt-5">
                {[
                  "Verifique consultas, confirmações e alertas em Operação do dia.",
                  "Conclua ou encaminhe as Metas do dia.",
                  "Leia o resumo e as ações do Assistente Executivo.",
                  "Revise o Consultor de Gestão conforme a periodicidade desejada.",
                  "Use Gestão do mês e Inteligência e previsão para decisões mais amplas.",
                  "Abra os atalhos para corrigir os dados diretamente na área de origem."
                ].map((item, index) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4"
                  >
                    <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      <strong>{index + 1}.</strong> {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-3xl p-8 mt-12">
            <h3 className="text-2xl font-bold text-green-900 mt-0 mb-4 flex items-center gap-3">
              <ShieldCheck size={26} />
              Boas práticas
            </h3>
            <ul className="space-y-3 mb-0">
              <li>mantenha agenda, faturas, recebimentos e despesas atualizados;</li>
              <li>não interprete um único indicador isoladamente;</li>
              <li>use os períodos de 30, 90 e 180 dias para distinguir variações pontuais de tendências;</li>
              <li>oculte blocos que não sejam relevantes para a função do usuário;</li>
              <li>utilize os atalhos para corrigir a informação na área de origem;</li>
              <li>revise alertas de dados parciais antes de tomar decisões.</li>
            </ul>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <LayoutDashboard size={26} />
              Recursos do Dashboard Executivo
            </h3>
            <ul className="space-y-3">
              {[
                "Visão integrada da operação, do financeiro e dos pacientes.",
                "Score geral da clínica com detalhamento.",
                "Proteção e ocultação de valores financeiros.",
                "Metas diárias com progresso individual.",
                "Assistente Executivo com alertas e ações.",
                "Consultor de Gestão com análises de 30, 90 e 180 dias.",
                "Modos Inteligente, Operacional, Financeiro e Crescimento.",
                "Personalização por usuário.",
                "Atalhos com filtros para as áreas relacionadas."
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-gray-100 rounded-3xl p-8 mt-8 shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mt-0 mb-3 flex items-center gap-2">
              <Lightbulb size={22} className="text-indigo-600" />
              Próximo passo recomendado
            </h3>
            <p>
              Muitos indicadores do Dashboard são alimentados pelos registros de
              faturas, recebimentos e despesas. Conheça o módulo Financeiro para
              manter essas informações completas e confiáveis.
            </p>
            <Link
              to="/ajuda/tutoriais/financeiro"
              className="not-prose inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-bold transition-colors"
            >
              Conhecer o Financeiro
              <ArrowRight size={18} />
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
}
